import React, { useRef, useMemo, useCallback, useEffect } from "react";
import _ from "lodash";
import {
  IconButton,
  Button,
  Dropdown,
  DropdownMenu,
  Tooltip,
} from "@toddle-edu/ds-web";
import { PauseFilled, PlayFilled, CopyOutlined } from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import { getPlaybackSpeedOptions } from "../../modules/GoogleDocsPlaybackViewerHelpers";
import classes from "./PlaybackFooterControls.scss";

const PlaybackFooterControls = ({
  steps,
  currentStep,
  setCurrentStep,
  autoPlay,
  setAutoPlay,
  isDragging,
  setIsDragging,
  playbackSpeed,
  handlePlaybackSpeedChange,
  width,
}) => {
  const { t } = useI18n(["classRoom"]);
  const progressBarRef = useRef(null);
  const PROGRESS_BAR_WIDTH = width - 144;
  const maxSteps = steps.length;

  //need to pass current width based on that paste positions also changes
  const pastePositions = useMemo(() => {
    const result = [];
    for (let i = 0; i < steps.length; i++) {
      if (steps[i]?.type === "paste") {
        const centerPosition = (i + 1) * (PROGRESS_BAR_WIDTH / steps.length);
        if (_.isEmpty(result)) {
          result.push({
            position: centerPosition,
            count: 1,
            firstIndex: i + 1,
          });
          continue;
        }
        const lastPastePosition = _.last(result).position;
        if (Math.abs(centerPosition - lastPastePosition) < 26) {
          // within the same group
          _.last(result).count += 1;
        } else {
          result.push({
            position: centerPosition,
            count: 1,
            firstIndex: i + 1,
          });
        }
      }
    }
    return result;
  }, [steps, PROGRESS_BAR_WIDTH]);

  const updateProgressFromEvent = useCallback(
    event => {
      const progressBar = progressBarRef.current;
      if (!progressBar) return;

      const rect = progressBar.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const progressWidth = rect.width;
      const progressPercentage = Math.max(
        0,
        Math.min(1, clickX / progressWidth)
      );
      const newStep = Math.round(progressPercentage * maxSteps);
      const clampedStep = Math.max(0, Math.min(newStep, maxSteps));
      setCurrentStep(clampedStep);
    },
    [maxSteps, setCurrentStep]
  );

  const handleProgressBarKeyDown = event => {
    event?.preventDefault();

    switch (event.key) {
      case "ArrowLeft":
        setCurrentStep(prev => Math.max(0, prev - 1));
        break;
      case "ArrowRight":
        setCurrentStep(prev => Math.min(maxSteps, prev + 1));
        break;
      case "Home":
        setCurrentStep(0);
        break;
      case "End":
        setCurrentStep(maxSteps);
        break;
      default:
        break;
    }
  };

  const handleMouseDown = e => {
    setIsDragging(true);
    updateProgressFromEvent(e);
  };

  // Handle mouse events with proper cleanup
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    const handleGlobalMouseMove = e => {
      if (isDragging && e?.buttons === 1) {
        updateProgressFromEvent(e);
      }
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging, setIsDragging, updateProgressFromEvent]);

  const playBackIcon =
    autoPlay && currentStep < maxSteps ? <PauseFilled /> : <PlayFilled />;

  return (
    <div className={classes.controls}>
      <IconButton
        dsVersion="2.0"
        onClick={() => setAutoPlay(prev => !prev)}
        icon={playBackIcon}
        variant="neutral"
        type="plain"
        size="medium"
      />

      <div
        className={classes.progressContainer}
        style={{ width: PROGRESS_BAR_WIDTH }}
      >
        <div className={classes.pasteIndicatorContainer}>
          {_.map(pastePositions, pasteGroup => (
            <div
              key={`paste-group-${pasteGroup.position}`}
              className={classes.pasteIndicator}
              style={{
                left: `${pasteGroup.position - 12}px`,
              }}
              onClick={() => setCurrentStep(pasteGroup.firstIndex)}
              onKeyDown={event => {
                if (event?.key === "Enter" || event?.key === " ") {
                  event.preventDefault();
                  setCurrentStep(pasteGroup.firstIndex);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Jump to paste at edit ${pasteGroup.firstIndex}`}
            >
              <Tooltip
                placement="top"
                tooltip={t("classRoom:paste_detected_with_count", {
                  count: pasteGroup.count,
                })}
              >
                <CopyOutlined variant="warning" size="xxx-small" />
              </Tooltip>
            </div>
          ))}
        </div>

        <div
          className={classes.progressBar}
          onClick={updateProgressFromEvent}
          onMouseDown={handleMouseDown}
          onKeyDown={handleProgressBarKeyDown}
          role="slider"
          tabIndex={0}
          aria-label="Playback progress"
          aria-valuemin={0}
          aria-valuemax={maxSteps}
          aria-valuenow={currentStep}
          ref={progressBarRef}
        >
          <div
            className={classes.progressFill}
            style={{
              width: `${maxSteps > 0 ? (currentStep / maxSteps) * 100 : 100}%`,
            }}
          >
            <div className={classes.progressThumb} />
          </div>
        </div>
      </div>

      <Dropdown
        dsVersion="2.0"
        overlay={
          <DropdownMenu
            dsVersion="2.0"
            showSelection={true}
            value={playbackSpeed}
            onClick={handlePlaybackSpeedChange}
            options={getPlaybackSpeedOptions({ t })}
          />
        }
      >
        <Button dsVersion="2.0" variant="neutral" type="plain" size="medium">
          {`${playbackSpeed}x`}
        </Button>
      </Dropdown>
    </div>
  );
};

export default PlaybackFooterControls;
